/**
 * 2027年度予算 v8 の事業部シートを貼り付けて、予算の表に入れる(2026-10-23 段階5・K7)。読み取りは budgetImport.ts。
 * ここでは表に入れるだけ。保存は予算の画面の「保存」ボタン(今までと同じ)。
 */
import { useMemo, useState } from 'react';
import Modal from '@core/components/Modal';
import { formatYen } from '@core/lib/money';
import type { Account, MonthStr } from '@core/lib/types';
import { parseV8Budget, splitPasted, type V8BudgetParse } from './budgetImport';

export default function V8BudgetImportModal({
  departmentName,
  fiscalYear,
  months,
  accounts,
  budgetAccountIds,
  onApply,
  onClose,
}: {
  departmentName: string;
  fiscalYear: number;
  months: MonthStr[];
  accounts: Account[];
  /** 予算の表にある科目(ここに無い科目には入れられない) */
  budgetAccountIds: Set<string>;
  onApply: (rows: Array<{ accountId: string; amounts: number[] }>) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState('');
  const [result, setResult] = useState<V8BudgetParse | null>(null);
  const accountName = (id: string) => accounts.find((a) => a.id === id)?.name ?? id;

  const judged = useMemo(
    () =>
      (result?.rows ?? []).map((r) => {
        let state: 'ok' | 'none' | 'notInTable' | 'negative' = 'ok';
        if (!r.accountId) state = 'none';
        else if (!budgetAccountIds.has(r.accountId)) state = 'notInTable';
        else if (r.amounts.some((a) => a < 0)) state = 'negative';
        return { ...r, state };
      }),
    [result, budgetAccountIds],
  );
  const usable = judged.filter((r) => r.state === 'ok');

  const STATE_LABEL = { ok: '入れる', none: 'マスタに無い', notInTable: '予算の表に無い', negative: 'マイナスあり' } as const;
  const STATE_BADGE = { ok: 'b-ok', none: 'b-ng', notInTable: 'b-ng', negative: 'b-pend' } as const;

  return (
    <Modal onClose={onClose} width="min(860px, 94vw)">
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
        <h3>
          v8 の予算を取り込む({departmentName}・{fiscalYear}年度)
        </h3>
      </div>
      <p className="hint" style={{ marginBottom: 10 }}>
        2027年度予算 v8 の事業部のシートを、見出しの行(1月〜12月)から下まで選んでコピーし、ここに貼り付けてください。
        科目②・明細の行を科目①ごとに足して入れます(「計」「率」「合計」の行は読みません)。入れたあと「保存」を押すまで保存されません。
      </p>
      <textarea
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setResult(null);
        }}
        placeholder="v8 のシートをここに貼り付け"
        rows={6}
        style={{ width: '100%', fontFamily: 'inherit', padding: 8, border: '1px solid var(--line)', borderRadius: 6 }}
      />

      {result?.error && (
        <p className="hint" style={{ color: 'var(--red)', margin: '10px 0 0' }}>
          {result.error}
        </p>
      )}
      {result?.warning && (
        <p className="hint" style={{ color: 'var(--red)', margin: '10px 0 0' }}>
          {result.warning}。予算の画面で年度を選び直してから取り込んでください(違う年度には入れません)。
        </p>
      )}

      {judged.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <p style={{ margin: '0 0 8px' }}>
            入れる {usable.length}科目(年間 ¥{formatYen(usable.reduce((s, r) => s + r.total, 0))})/ 入れない {judged.length - usable.length}科目
          </p>
          <table>
            <thead>
              <tr>
                <th>区分</th>
                <th>v8 の科目①</th>
                <th>この画面の科目</th>
                <th className="num">年間合計</th>
                <th>判定</th>
              </tr>
            </thead>
            <tbody>
              {judged.map((r) => (
                <tr key={`${r.kubun}|${r.name}`}>
                  <td>{r.kubun || '—'}</td>
                  <td>{r.name}</td>
                  <td>{r.accountId ? accountName(r.accountId) : '—'}</td>
                  <td className="num">{formatYen(r.total)}</td>
                  <td>
                    <span className={`badge ${STATE_BADGE[r.state]}`}>{STATE_LABEL[r.state]}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {judged.length > usable.length && (
            <p className="hint" style={{ marginTop: 8 }}>
              「マスタに無い」「予算の表に無い」科目は入れません(勘定科目マスタの見直し U15 で扱いを決めます)。マイナスの予算(値引きなど)は今の予算の表に入れられません。
            </p>
          )}
        </div>
      )}

      <div className="modal-actions">
        <button
          className="btn-line"
          onClick={() => setResult(parseV8Budget({ table: splitPasted(text), months, accounts }))}
          disabled={!text.trim()}
        >
          内容を確かめる
        </button>
        <button
          className="btn-green"
          onClick={() => {
            onApply(usable.map((r) => ({ accountId: r.accountId!, amounts: r.amounts })));
            onClose();
          }}
          disabled={usable.length === 0 || !!result?.warning}
        >
          {usable.length}科目を表に入れる
        </button>
      </div>
    </Modal>
  );
}
